import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import TablePaginationActions from "../../globals/components/TablePaginationActions";
import { bondTabs, getUnderwritingChip } from "../hooks/useApproveBonds";

export default function ApproveBondsMainBondsPanel({
  tabIndex,
  loading,
  filteredData,
  currentRows,
  formatBondAmount,
  refreshData,
  page,
  handleChangePage,
  rowsPerPage,
  handleChangeRowsPerPage,
  setSelectedBondToCancel,
  setCancellationFile,
  setCancelDialogOpen,
  setSelectedBondToEndorse,
  setEndorsementType,
  setNewBondAmount,
  setEndorsementFile,
  setUpdatedName,
  setEndorsementToCounty,
  setEstimatedAssets,
  setDwbFirstName,
  setDwbMiddleName,
  setDwbLastName,
  setDwbSuffix,
  setDwbAlias,
  setEndorsementDialogOpen,
}) {
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px" }}>
        <CircularProgress />
      </Box>
    );
  }

  function getEmptyTableColSpan() {
    const currentTab = bondTabs[tabIndex]?.toLowerCase() || "active";
    if (currentTab === "to be cancelled") return 9;
    const isActive = currentTab === "active";
    const isApproved = currentTab === "approved";
    const isDeclined = currentTab === "rejected";
    let cols = 9;
    if (isApproved || isDeclined) cols += 1;
    if (isActive || isApproved) cols += 1;
    return cols;
  }

  function renderToBeCancelledRows() {
    return currentRows.map((bond, index) => (
      <TableRow
        key={
          bond.bond_id ??
          bond.id ??
          bond.bond_number ??
          `approve-cancel-${index}`
        }
      >
        <TableCell>{bond.bond_type}</TableCell>
        <TableCell>{bond.bond_sub_category}</TableCell>
        <TableCell>{bond.name}</TableCell>
        <TableCell>{Array.isArray(bond.principals) ? bond.principals.join(", ") : (bond.principals || "N/A")}</TableCell>
        <TableCell>{bond.bond_number}</TableCell>
        <TableCell align="right">{formatBondAmount(bond.bond_amount)}</TableCell>
        <TableCell>{bond.law_firm_name}</TableCell>
        <TableCell>{bond.law_firm_user_name}</TableCell>
        <TableCell align="center">
          <Stack direction="row" spacing={0.5} justifyContent="center">
            <Tooltip title="View Bond" arrow>
              <IconButton
                color="primary"
                onClick={() => {
                  let url = "";
                  if (bond.bond_type === "VA") {
                    url = `/vermost-va-bond-update/${bond.bond_id}`;
                  } else if (bond.bond_type === "Trustee") {
                    url = `/vermost-trustee-bond-update/${bond.bond_id}`;
                  } else if (bond.bond_type === "Probate") {
                    url = `/vermost-probate-bond-update/${bond.bond_id}`;
                  } else if (bond.bond_type === "Blanket") {
                    url = `/vermost-blanket-bond-update/${bond.bond_id}`;
                  } else {
                    url = `/vermost-guardianship-bond-update/${bond.bond_id}`;
                  }
                  if (url) {
                    window.open(url, "_blank");
                  }
                }}
              >
                <VisibilityIcon />
              </IconButton>
            </Tooltip>
            {bond.cancellation_document_link && (
              <Tooltip title="Download Cancellation Documents" arrow>
                <IconButton
                  sx={{ color: "grey.600" }}
                  onClick={() => {
                    window.open(bond.cancellation_document_link, "_blank");
                  }}
                >
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Cancel Bond">
              <IconButton
                color="error"
                onClick={() => {
                  setSelectedBondToCancel(bond);
                  setCancellationFile(null);
                  setCancelDialogOpen(true);
                }}
              >
                <CancelIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </TableCell>
      </TableRow>
    ));
  }

  function renderRegularBondRows() {
    return currentRows.map((bond, index) => {
      const currentTab = bondTabs[tabIndex]?.toLowerCase() || "active";
      const isActive = currentTab === "active";
      const isApproved = currentTab === "approved";
      const isDeclined = currentTab === "rejected";

      const getBondUrl = () => {
        if (bond.bond_type === "VA") {
          return `/vermost-va-bond-update/${bond.bond_id}`;
        }
        if (bond.bond_type === "Trustee") {
          return `/vermost-trustee-bond-update/${bond.bond_id}`;
        }
        if (bond.bond_type === "Probate") {
          return `/vermost-probate-bond-update/${bond.bond_id}`;
        }
        if (bond.bond_type === "Blanket") {
          return `/vermost-blanket-bond-update/${bond.bond_id}`;
        }
        return `/vermost-guardianship-bond-update/${bond.bond_id}`;
      };

      return (
        <TableRow
          key={
            bond.bond_id ??
            bond.id ??
            bond.bond_number ??
            `approve-row-${index}`
          }
        >
          <TableCell>{bond.bond_type}</TableCell>
          <TableCell>{bond.bond_sub_category || "N/A"}</TableCell>
          {(isApproved || isDeclined) && (
            <TableCell>{bond.bond_number || "N/A"}</TableCell>
          )}
          <TableCell align="left">
            {(() => {
              const chip = getUnderwritingChip(bond.underwriting_status);
              return (
                <Chip
                  size="small"
                  label={chip.label}
                  color={chip.color}
                  variant={chip.color === "default" ? "outlined" : "filled"}
                />
              );
            })()}
          </TableCell>
          <TableCell>{bond.name}</TableCell>
          <TableCell align="right">
            {bond.last_endorsement_at ? (
              <Typography variant="body2" color="primary">
                Endorsement Request:{" "}
                {formatBondAmount(bond.bond_amount)}
              </Typography>
            ) : (
              formatBondAmount(bond.bond_amount)
            )}
          </TableCell>
          <TableCell>{bond.principal_one}</TableCell>
          <TableCell>{bond.law_firm_name || "N/A"}</TableCell>
          <TableCell>{bond.law_firm_user_name || "N/A"}</TableCell>
          <TableCell>
            {bond.referral_source ? (
              <Tooltip title={bond.referred_by ? `Referred by: ${bond.referred_by}` : ""} arrow>
                <Typography variant="body2" sx={{ fontSize: "0.8rem", maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {bond.referral_source}
                </Typography>
              </Tooltip>
            ) : "—"}
          </TableCell>
          {(isActive || isApproved) && (
            <TableCell align="center">
              <Tooltip title="View Bond" arrow>
                <IconButton
                  color="primary"
                  onClick={() => {
                    const url = getBondUrl();
                    if (url) {
                      window.open(url, "_blank");
                    }
                  }}
                >
                  <VisibilityIcon />
                </IconButton>
              </Tooltip>
              {isActive && (
                <Tooltip title="Bond Endorsement" arrow>
                  <IconButton
                    color="secondary"
                    onClick={() => {
                      setSelectedBondToEndorse(bond);
                      setEndorsementType("Increase");
                      setNewBondAmount("");
                      setEndorsementFile(null);
                      setUpdatedName("");
                      setEndorsementToCounty("");
                      setEstimatedAssets("");
                      setDwbFirstName("");
                      setDwbMiddleName("");
                      setDwbLastName("");
                      setDwbSuffix("");
                      setDwbAlias("");
                      setEndorsementDialogOpen(true);
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              )}
            </TableCell>
          )}
        </TableRow>
      );
    });
  }

  function renderTableBody() {
    if (filteredData.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={getEmptyTableColSpan()} align="center">
            <Box sx={{ py: 5 }}>
              <Typography variant="h6" sx={{ textAlign: "center", fontWeight: 800 }}>
                No results
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", mt: 0.5 }}>
                Try a different search term or switch tabs.
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                <Button onClick={refreshData} startIcon={<RefreshIcon />} variant="outlined">
                  Refresh
                </Button>
              </Box>
            </Box>
          </TableCell>
        </TableRow>
      );
    }
    if (bondTabs[tabIndex]?.toLowerCase() === "to be cancelled") {
      return renderToBeCancelledRows();
    }
    return renderRegularBondRows();
  }

  return (
    <>
      <TableContainer component={Paper} sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow
              sx={{
                "& th": {
                  backgroundColor: "#f0f0f0",
                  color: "#111",
                  fontWeight: 700,
                  borderBottomColor: "rgba(0,0,0,0.12)",
                },
              }}
            >
              {bondTabs[tabIndex]?.toLowerCase() === "to be cancelled" ? (
                <>
                  <TableCell>Bond Type</TableCell>
                  <TableCell>Sub Category</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Principals</TableCell>
                  <TableCell>Bond Number</TableCell>
                  <TableCell align="right">Bond Amount</TableCell>
                  <TableCell>Law Firm Name</TableCell>
                  <TableCell>Attorney</TableCell>
                  <TableCell align="center">Action</TableCell>
                </>
              ) : (() => {
                const currentTab = bondTabs[tabIndex]?.toLowerCase() || "active";
                const isActive = currentTab === "active";
                const isApproved = currentTab === "approved";
                const isDeclined = currentTab === "rejected";

                return (
                  <>
                    <TableCell>Bond Type</TableCell>
                    <TableCell>Sub Category</TableCell>
                    {(isApproved || isDeclined) && (
                      <TableCell>Bond Number</TableCell>
                    )}
                    <TableCell align="left">Underwriting</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell align="right">Bond Amount</TableCell>
                    <TableCell>Principal</TableCell>
                    <TableCell>Law Firm Name</TableCell>
                    <TableCell>Attorney</TableCell>
                    <TableCell>Referral</TableCell>
                    {(isActive || isApproved) && (
                      <TableCell align="center">Action</TableCell>
                    )}
                  </>
                );
              })()}
            </TableRow>
          </TableHead>
          <TableBody sx={{ '& tr:nth-of-type(odd)': { backgroundColor: '#fafafa' } }}>
            {renderTableBody()}
          </TableBody>
        </Table>
      </TableContainer>

      {bondTabs[tabIndex]?.toLowerCase() !== "to be cancelled" && (
        <TablePagination
          ActionsComponent={TablePaginationActions}
          component="div"
          count={filteredData.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
          sx={{ mt: 2 }}
        />
      )}
    </>
  );
}

ApproveBondsMainBondsPanel.propTypes = {
  tabIndex: PropTypes.number.isRequired,
  loading: PropTypes.bool.isRequired,
  filteredData: PropTypes.array.isRequired,
  currentRows: PropTypes.array.isRequired,
  formatBondAmount: PropTypes.func.isRequired,
  refreshData: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  handleChangePage: PropTypes.func.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  handleChangeRowsPerPage: PropTypes.func.isRequired,
  setSelectedBondToCancel: PropTypes.func.isRequired,
  setCancellationFile: PropTypes.func.isRequired,
  setCancelDialogOpen: PropTypes.func.isRequired,
  setSelectedBondToEndorse: PropTypes.func.isRequired,
  setEndorsementType: PropTypes.func.isRequired,
  setNewBondAmount: PropTypes.func.isRequired,
  setEndorsementFile: PropTypes.func.isRequired,
  setUpdatedName: PropTypes.func.isRequired,
  setEndorsementToCounty: PropTypes.func.isRequired,
  setEstimatedAssets: PropTypes.func.isRequired,
  setDwbFirstName: PropTypes.func.isRequired,
  setDwbMiddleName: PropTypes.func.isRequired,
  setDwbLastName: PropTypes.func.isRequired,
  setDwbSuffix: PropTypes.func.isRequired,
  setDwbAlias: PropTypes.func.isRequired,
  setEndorsementDialogOpen: PropTypes.func.isRequired,
};
